import type { ShareScope } from '@mathflat/domain/@entities/(Content)/constants'
import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import { Entity as ProblemEntity } from '@mathflat/domain/@entities/Problem/dto'
import { computed, makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'

import { DTO } from '../DTO'

export type ProblemAddSource = '새문제추가' | '쌍둥이유사' | '즐겨찾기' | '나의DB'

/**
 * @deprecated
 * 실제 problem과는 다른 정보로 이해하기 힘든 god object가 되어버려서 삭제 예정
 */
export class _MakeProblem extends DTO {
  id = EMPTY_NUMBER_ID // 문제 아이디 또는 커리큘럼, 개념 아이디
  type: common.ProblemType = 'SINGLE_CHOICE'
  conceptId = EMPTY_NUMBER_ID // 개념 아이디
  curriculumId = EMPTY_NUMBER_ID // 교육과정 아이디
  problemImageUrl = '' // 문제 이미지 또는 개념 이미지
  answerImageUrl = '' // 답 이미지
  solutionImageUrl = '' // 해설 이미지

  rate = 50 // 정답률
  conceptName = '' // 유형 이름
  itemType: 'PROBLEM' | 'CONCEPT' | 'MIDDLE_CHAPTER_CONCEPT' = 'PROBLEM' // 종류  - 문제 / 개념 / 중단원 개념
  level: common.ProblemLevel = 3 //  문제 난이도
  trendy = false // 신경향 문제 여부

  // 만들기에서 기본이 CUSTOM이지만, 학습지만들기 - 시중교재일경우 PUBLIC임 - 문제신고시 분기처리
  problemType: 'CUSTOM' | 'PUBLIC' | 'CONCEPT' = 'CUSTOM' // 문제의 종류(저작권 관련) - CUSTOM : 자체문제 / PUBLIC: 시중 문제 / CONCEPT: 개념
  favorite = false // 즐겨찾기 했는지 여부값
  tagTop: res.Problem<'WORKSHEET'>['tagTop'] = null // 시중교재, 쌍둥이 문제 등 정보를 보여주는 string
  baseWorkbookProblemId: number | null = null
  defaultOrder: number | null = null // 서버에서 내려주는 초기 문제 정렬
  sequence = 0 // 서버에서 내려주는 정렬 내 순서

  autoScored = false
  // 현재는 'POSSIBLE_PLUS만 자동채점 가능
  autoScoredType: common.AutoScoredType = 'IMPOSSIBLE' // 단위가 포함된 정수값
  school: common.SchoolType | null = null
  conceptKey: string | null = null
  littleChapterName: string | null = null
  middleChapterName: string | null = null
  problemSource: res.Problem<'WORKSHEET'>['problemSource'] | null = null
  boxIndex: number | null = null
  workbookPaperProblemSource: res.Problem<'WORKBOOK'>['problemSource'] | null = null
  sourceType?: common.ProblemOriginType = undefined
  // 워크시트 추가 출처 (추천문제종류 집계용). 미지정(undefined)은 Default 추천문제. problemSource/sourceType 와 별개
  addSource?: ProblemAddSource = undefined
  myDbProblemDetailId: res.MyDbProblemDetail['detailId'] | null = null
  myDbPaperShareScope: Extract<ShareScope, 'ACADEMY' | 'TEACHER'> | null = null

  _concept?: CurriculumApi.Response.CurriculumInfo

  constructor(data: Partial<Omit<_MakeProblem, 'id'>> & { id?: _MakeProblem['id'] | null }) {
    super()
    if (data) {
      Object.assign(this, data)
      // 미복사 나의DB 문항은 problem id 미발급 상태라 detailId 기반 가상 음수 키로 컬렉션에 키잉한다.
      // detailId(>=1) 를 EMPTY_NUMBER_ID 아래로 깔아 sentinel(-1)·실 problem id 와 충돌하지 않게 한다
      this.id = data.id ?? EMPTY_NUMBER_ID - (this.myDbProblemDetailId ?? 0)
    }

    makeObservable(this, {
      id: observable,
      conceptId: observable,
      curriculumId: observable,
      type: observable,
      problemImageUrl: observable,
      answerImageUrl: observable,
      solutionImageUrl: observable,
      rate: observable,
      conceptName: observable,
      level: observable,
      tagTop: observable,
      problemType: observable,
      favorite: observable,
      defaultOrder: observable,
      sequence: observable,
      baseWorkbookProblemId: observable,
      autoScored: observable,
      autoScoredType: observable,
      school: observable,
      trendy: observable,
      conceptKey: observable,
      koLevel: computed,
      koAnswerType: computed,
      tagTopList: computed,
      middleChapterName: observable,
      littleChapterName: observable,
      problemSource: observable,
      workbookPaperProblemSource: observable,
      sourceType: observable,
      myDbProblemDetailId: observable,
      myDbPaperShareScope: observable,
      boxIndex: observable,
      _concept: observable,
    })
  }

  get koLevel() {
    return ProblemEntity.Problem.getKoLevel(this.level)
  }

  get koAnswerType() {
    return ProblemEntity.Problem.getKoAnswerType(this.type)
  }

  get tagTopList() {
    return ProblemEntity.Problem.getTagTopList(this.tagTop)
  }
}
